/**
 * Draggable wind rose. The needle points at the direction the wind comes
 * *from*, which is the meteorological convention pilots and forecasts use.
 * Grab anywhere on the dial and drag; hold Shift to snap to 5°.
 */

import { useCallback, useRef } from 'react';

interface Props {
  /** meteorological degrees — where the wind comes from */
  value: number;
  onChange: (deg: number) => void;
  size?: number;
  disabled?: boolean;
}

const CARDINALS: [string, number][] = [
  ['N', 0],
  ['NE', 45],
  ['E', 90],
  ['SE', 135],
  ['S', 180],
  ['SW', 225],
  ['NW', 315],
  ['W', 270],
];

export function CompassDial({ value, onChange, size = 168, disabled }: Props) {
  const ref = useRef<SVGSVGElement>(null);
  const r = size / 2;
  const ring = r - 20;

  const pointerToDeg = useCallback((clientX: number, clientY: number): number => {
    const rect = ref.current!.getBoundingClientRect();
    const dx = clientX - (rect.left + rect.width / 2);
    const dy = clientY - (rect.top + rect.height / 2);
    // Screen y is down; compass 0° is up and increases clockwise.
    const deg = (Math.atan2(dx, -dy) * 180) / Math.PI;
    return (deg + 360) % 360;
  }, []);

  const handle = useCallback(
    (e: React.PointerEvent) => {
      if (disabled) return;
      const deg = pointerToDeg(e.clientX, e.clientY);
      onChange(e.shiftKey ? Math.round(deg / 5) * 5 : Math.round(deg));
    },
    [disabled, onChange, pointerToDeg]
  );

  const onPointerDown = (e: React.PointerEvent) => {
    if (disabled) return;
    (e.target as Element).setPointerCapture(e.pointerId);
    handle(e);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (e.buttons === 0) return;
    handle(e);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    const step = e.shiftKey ? 10 : 1;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault();
      onChange((value - step + 360) % 360);
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      onChange((value + step) % 360);
    }
  };

  const rad = ((value - 90) * Math.PI) / 180;
  // Needle tail sits at the "from" bearing; the head shows where air travels.
  const fromX = r + Math.cos(rad) * ring;
  const fromY = r + Math.sin(rad) * ring;
  const toX = r - Math.cos(rad) * ring * 0.72;
  const toY = r - Math.sin(rad) * ring * 0.72;

  return (
    <div className="compass-wrap">
      <svg
        ref={ref}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className={`compass${disabled ? ' is-disabled' : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onKeyDown={onKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="slider"
        aria-label="Wind direction (degrees the wind comes from)"
        aria-valuenow={Math.round(value)}
        aria-valuemin={0}
        aria-valuemax={359}
        aria-valuetext={`${Math.round(value)} degrees`}
      >
        <circle cx={r} cy={r} r={ring + 12} className="compass-face" />
        {Array.from({ length: 72 }, (_, i) => {
          const a = ((i * 5 - 90) * Math.PI) / 180;
          const major = i % 9 === 0;
          const inner = ring + (major ? 2 : 6);
          const outer = ring + 11;
          return (
            <line
              key={i}
              x1={r + Math.cos(a) * inner}
              y1={r + Math.sin(a) * inner}
              x2={r + Math.cos(a) * outer}
              y2={r + Math.sin(a) * outer}
              className={major ? 'tick major' : 'tick'}
            />
          );
        })}
        {CARDINALS.map(([label, deg]) => {
          const a = ((deg - 90) * Math.PI) / 180;
          return (
            <text
              key={label}
              x={r + Math.cos(a) * (ring - 14)}
              y={r + Math.sin(a) * (ring - 14)}
              className={`compass-label${label.length === 1 ? ' cardinal' : ''}`}
              textAnchor="middle"
              dominantBaseline="central"
            >
              {label}
            </text>
          );
        })}

        <line x1={fromX} y1={fromY} x2={toX} y2={toY} className="needle" />
        <circle cx={fromX} cy={fromY} r={7} className="needle-grip" />
        <circle cx={r} cy={r} r={3.5} className="needle-hub" />
      </svg>
      <div className="compass-readout">
        <strong>{Math.round(value)}°</strong>
        <span>from {compassName(value)}</span>
      </div>
    </div>
  );
}

export function compassName(deg: number): string {
  const names = [
    'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
    'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW',
  ];
  return names[Math.round((((deg % 360) + 360) % 360) / 22.5) % 16];
}
